// MARK://dhk8/pos/features/pos/FastBarPage
// Modo rápido (barra / to-go): pantalla simplificada sin mesas, sin sub-cuentas.
// Catálogo → carrito → cobrar → orden TAKEOUT que se crea, envía y cierra en un paso
// (orchestrated client-side via order-store.createFastSale — see TODO backend there).
// Port de sc-restaurant features/pos/FastBarPage.tsx onto the new stack.

// TODO sibling A: register route `/pos/barra` → <FastBarPage/> (inside <Shell>) in App.tsx,
// and add a nav entry for it in PosLayout (icon: Coffee, label: "Barra").

import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Minus, Trash2, Search as SearchIcon, Coffee, Banknote, Sparkles, Monitor, Ban, Maximize2 } from 'lucide-react'
import { Badge, Button } from '@dockethub/ui'
import type { ProductDto, ProductWithStock } from '@dockethub/ui-restaurant'
import { formatCents } from '../../lib/money.js'
import { useActiveMenu, useSetProductAvailability } from '../../hooks/useCatalog.js'
import { useCart } from '../../hooks/use-cart.js'
import { useOrderStore, type DraftItem } from '../../stores/order-store.js'
import { previewProductExtras } from '../../lib/seed-preview.js'
import AddItemDialog from '../AddItemDialog.js'
import PaymentDialog from '../PaymentDialog.js'
import BottomSheet from './BottomSheet.js'
import { getReceiptOpts } from '../../lib/print/receipt-opts.js'
import { useBroadcastCart, type CartSnapshot } from '../../hooks/use-cart-broadcast.js'
import { useTenantId } from '../../hooks/useTenantId.js'
import { getBranding } from '../../lib/branding-loader.js'
import { useAuthStore } from '../../stores/auth-store.js'
import { getPrintingConfig, pushPrintHistory } from '../../lib/bridge/printing-config-store.js'
import { printReceipt } from '../../lib/bridge/bridge-client.js'
import { trackRelayJob } from '../../lib/bridge/relay-job-tracker.js'
import { toReceipt } from '../../lib/bridge/canonical-receipt.js'


export default function FastBarPage() {
  const menu = useActiveMenu()
  const openFastSaleOrder = useOrderStore((s) => s.openFastSaleOrder)
  const cart = useCart()

  const [search, setSearch] = useState('')
  const [activeCat, setActiveCat] = useState<string | 'ALL'>('ALL')
  // Cobro: la orden se crea (openFastSaleOrder) e hidrata el store, luego el
  // PaymentDialog cobra en múltiples tenders hasta cerrar (pago mixto).
  const [paymentOpen, setPaymentOpen] = useState(false)
  // Orden "maximizada": en móvil la lista inline (peek) se vuelve incómoda con 3+
  // ítems; este flag abre un BottomSheet con la orden completa (paridad comanda).
  const [cartMaxOpen, setCartMaxOpen] = useState(false)
  const [pickedProduct, setPickedProduct] = useState<ProductDto | null>(null)
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null)
  const showToast = (msg: string, kind: 'success' | 'error' = 'success') => {
    setToast({ msg, kind }); setTimeout(() => setToast(null), 2500)
  }

  // ── Pantalla de cliente (Barra · 2 pantallas) ──────────────────────────────
  // El cajero abre una segunda ventana del navegador en el monitor del cliente
  // (/barra-display). Para sincronizar, broadcasteamos un snapshot del carrito
  // por BroadcastChannel del mismo navegador — sin backend, sin WebSocket.
  // `clientWin` recuerda la referencia a la ventana abierta para reusarla y
  // tenerla bajo `window.focus()` si el cajero vuelve a presionar el botón.
  const [clientWin, setClientWin] = useState<Window | null>(null)
  const clientOpen = !!clientWin && !clientWin.closed
  // Detectar cierre de la ventana del cliente (poll cada 2s — el navegador
  // no expone evento para `closed`).
  useEffect(() => {
    if (!clientWin) return
    const id = setInterval(() => {
      if (clientWin.closed) setClientWin(null)
    }, 2_000)
    return () => clearInterval(id)
  }, [clientWin])

  function openClientDisplay() {
    // Si ya hay una ventana abierta, sólo enfócala. Eso permite que el cajero
    // vuelva a tocar el botón sin perder estado.
    if (clientWin && !clientWin.closed) { try { clientWin.focus() } catch { /* ignore */ } ; return }
    const w = typeof window !== 'undefined'
      ? window.open('/barra-display', 'dhk8-barra-display', 'noopener=no,width=1280,height=800')
      : null
    if (w) setClientWin(w)
  }

  const products = menu.data?.products ?? []
  const categories = menu.data?.categories ?? []

  // 86 / agotar producto: toggle de disponibilidad en servicio. Override optimista
  // local por productId (el `menuScreen` no expone `active` aún). En la barra NO
  // ocultamos los agotados: los dejamos visibles pero atenuados con un badge "86"
  // para que el cajero pueda reactivarlos de un toque.
  const setAvail = useSetProductAvailability()
  const [availOverride, setAvailOverride] = useState<Record<string, boolean>>({})
  const [availBusyId, setAvailBusyId] = useState<string | null>(null)
  const isAvailable86 = (p: ProductWithStock): boolean => {
    const o = availOverride[p.id]
    return o !== undefined ? o : p.isAvailable !== false
  }
  async function toggleAvailability(productId: string, currentlyAvailable: boolean) {
    const next = !currentlyAvailable
    setAvailOverride((m) => ({ ...m, [productId]: next }))
    setAvailBusyId(productId)
    try {
      const res = await setAvail.mutateAsync({ productId, available: next })
      setAvailOverride((m) => ({ ...m, [productId]: res.active }))
      showToast(res.active ? 'Producto disponible' : 'Producto marcado como agotado (86)')
    } catch (e) {
      setAvailOverride((m) => ({ ...m, [productId]: currentlyAvailable }))
      showToast(e instanceof Error ? e.message : 'No se pudo cambiar la disponibilidad', 'error')
    } finally {
      setAvailBusyId(null)
    }
  }

  const filtered = useMemo(() => {
    let list = products
    if (activeCat !== 'ALL') list = list.filter((p) => p.categoryId === activeCat)
    const q = search.trim().toLowerCase()
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q))
    return list
  }, [products, activeCat, search])

  const subtotal = cart.subtotalCents
  const itemCount = cart.itemCount

  // Snapshot del carrito para broadcast → pantalla de cliente.
  // FastBar no aplica promos a nivel carrito (eso vive en flujos de cuenta), así
  // que `promos` va vacío y `totalCents === subtotalCents`. La pantalla de
  // cliente sabe ocultar la sección de promos cuando no hay nada que mostrar.
  const snapshot: CartSnapshot = useMemo(() => {
    const brand = getBranding()
    return {
      v: 1,
      ts: Date.now(),
      branding: { name: brand.name, logoUrl: brand.logoUrl },
      lines: cart.lines,
      subtotalCents: subtotal,
      promos: [],
      totalCents: subtotal,
      lastSale: null,
    }
  }, [cart.lines, subtotal])
  // S1-5 hardening 2026-05-24: el canal se scopa por tenantId activo para
  // que multi-tenant en el mismo browser (e.g. SuperAdmin abriendo 2 sucursales
  // distintas) NO cruce carts entre Customer Displays.
  const broadcastTenantId = useTenantId()
  useBroadcastCart(snapshot, clientOpen, broadcastTenantId)

  // El ticket "Venta cerrada" lo muestra ahora PaymentDialog (mismo renderReceipt
  // branded). tenantIdActive se conserva para el auto-print de barra en onClosed.
  const tenantIdActive = useAuthStore((s) => s.activeTenantId())

  /** True when a product has variant / modifier groups según el preview stub.
   *  Solo se usa para el badge "Personalizar" en la card (cosmético). El stub
   *  solo conoce SKUs de demo; para productos reales devuelve false aunque el
   *  backend SÍ tenga modifier groups linkeados. El AddItemDialog hace su propio
   *  fetch real cuando se abre — no depende de este check. */
  function isCustomizable(productId: string): boolean {
    const ex = previewProductExtras(productId)
    return !!ex.variants || (ex.modifierGroups?.length ?? 0) > 0
  }

  /** Tap a menu product: SIEMPRE abrir AddItemDialog. El propio dialog fetchea
   *  modifier groups del backend (`listModifierGroups({productId})`) y renderiza:
   *   - producto con grupos → opciones de personalización + qty + Agregar
   *   - producto sin grupos → solo qty + notas + Agregar (1 tap extra que el
   *     plain-add original ahorraba, vs el bug de no mostrar modifiers reales).
   *  Patrón espejo de OrderPage.handleSelectProduct (que también SIEMPRE abre).
   *  Antes: si `previewProductExtras` (stub demo) decía sin extras, agregabamos
   *  directo al carrito SIN mostrar dialog — el barista nunca veía los modifiers
   *  reales de productos SAVIs (UUIDs no conocidos por el stub). */
  function handleSelectProduct(p: ProductDto) {
    // 86 / agotar: un producto agotado no se puede agregar al carrito. El cajero
    // debe reactivarlo primero (toque en el badge "86" de la card).
    if (availOverride[p.id] === false) { showToast('Producto agotado (86) — reactívalo para venderlo', 'error'); return }
    setPickedProduct(p)
  }

  async function handleAddCustomized(draft: DraftItem) {
    // `draft.unitPriceCents` from AddItemDialog is the BASE unit price (product +
    // variant) — the modifier deltas live in `draft.modifiers`. The fast-bar cart
    // (use-cart) expects `unitPriceCents` to ALREADY include the modifier deltas
    // (its `modifiers[]` is display-only and createFastSale sends `modifiers: []`
    // to the backend), so fold the deltas in here. Without this the fast-bar
    // would under-charge every customized line.
    const modDelta = draft.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0)
    cart.addDraft({
      productId: draft.productId, name: draft.name, unitPriceCents: draft.unitPriceCents + modDelta,
      taxRate: draft.taxRate, quantity: draft.quantity,
      modifiers: draft.modifiers.map((m) => ({ name: m.name, priceDeltaCents: m.priceDeltaCents })),
      ...(draft.variantId ? { variantId: draft.variantId } : {}),
      ...(draft.variantName ? { variantName: draft.variantName } : {}),
      ...(draft.notes ? { notes: draft.notes } : {}),
    })
  }

  async function handleCharge() {
    if (cart.lines.length === 0) { showToast('Agrega al menos un producto', 'error'); return }
    setCartMaxOpen(false) // si venimos del sheet maximizado, ciérralo antes del checkout
    try {
      // Crea la orden TAKEOUT (SIN fire) + hidrata el store; PaymentDialog cobra
      // en múltiples tenders. unitPriceCents del cart YA tiene los deltas foldados
      // → desfoldeamos el base (el backend recomputa gross = base + Σdeltas; mandar
      // foldado + modifiers duplicaría el cobro). Mismo desfolde que la venta previa.
      await openFastSaleOrder({
        lines: cart.lines.map((l) => {
          const modSum = l.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0)
          return {
            productId: l.productId, name: l.name, quantity: l.quantity,
            unitPriceCents: l.unitPriceCents - modSum, taxRate: l.taxRate,
            modifiers: l.modifiers.map((m) => ({ name: m.name, priceDeltaCents: m.priceDeltaCents })),
            ...(l.notes ? { notes: l.notes } : {}),
          }
        }),
      })
      setPaymentOpen(true)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo abrir el cobro', 'error')
    }
  }

  // ── Persistent focus on the SKU/search input (retail barcode-gun pattern) ────
  // Hardware scanners type characters into the focused field and finish with Enter.
  // We auto-focus on mount and re-focus after every modal closes / after each cart
  // add. If the user clicks another button/input, focus moves naturally — we don't
  // fight that; the next "return to search" event (modal close, add) restores it.
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const focusSearch = () => {
    // Microtask: let the closing dialog unmount first so focus actually lands.
    queueMicrotask(() => {
      const el = searchInputRef.current
      if (!el) return
      el.focus()
      el.select?.()
    })
  }
  useEffect(() => { focusSearch() }, [])
  // Whenever the AddItemDialog or PaymentDialog closes, return focus to the input.
  const wasPickedOpen = useRef(false)
  useEffect(() => {
    if (pickedProduct) { wasPickedOpen.current = true; return }
    if (wasPickedOpen.current) { wasPickedOpen.current = false; focusSearch() }
  }, [pickedProduct])
  const wasCheckoutOpen = useRef(false)
  useEffect(() => {
    if (paymentOpen) { wasCheckoutOpen.current = true; return }
    if (wasCheckoutOpen.current) { wasCheckoutOpen.current = false; focusSearch() }
  }, [paymentOpen])

  /** Enter handler scoped to the search input. SKU exact match (case-insensitive)
   *  wins; otherwise if `filtered` collapses to a single product, take that one.
   *  0 or >1 matches → noop, let the user keep typing/filtering. Customizable
   *  products still flow through handleSelectProduct → opens AddItemDialog. */
  function handleSearchEnter() {
    const q = search.trim()
    if (!q) return
    const qLower = q.toLowerCase()
    const exactSku = filtered.find((p) => (p.sku ?? '').toLowerCase() === qLower)
    const picked = exactSku ?? (filtered.length === 1 ? filtered[0] : null)
    if (!picked) return
    handleSelectProduct(picked as unknown as ProductDto)
    setSearch('')
    // handleSelectProduct ahora SIEMPRE abre el dialog — el focus regresa al
    // search input cuando el dialog cierra (useEffect wasPickedOpen abajo).
  }

  // El carrito (lista + pie con Total/Cobrar) se renderiza en DOS contenedores: el
  // aside inline (peek glanceable, Cobrar siempre visible) y el BottomSheet
  // "maximizado" en móvil (lista completa navegable). Se extrae a variables para no
  // duplicar el JSX ni los handlers.
  const cartListEl = (
    <ul className="dhk8-fb__cart-list">
      {cart.lines.length === 0 ? (
        <li className="dhk8-fb__cart-empty">Toca un producto para agregarlo.</li>
      ) : cart.lines.map((l) => (
        <li key={l.lineId} className="dhk8-fb__cart-item">
          <div className="dhk8-fb__cart-item-head">
            <span className="dhk8-fb__cart-qty">×{l.quantity}</span>
            <span className="dhk8-fb__cart-name">
              {l.name}
              {l.modifiers.length > 0 && <span className="dhk8-fb__cart-mods">{l.modifiers.map((m) => m.name).join(' · ')}</span>}
              {l.notes && <span className="dhk8-fb__cart-mods dhk8-fb__cart-mods--note">“{l.notes}”</span>}
            </span>
            <span className="dhk8-fb__cart-total">{formatCents(l.unitPriceCents * l.quantity)}</span>
          </div>
          <div className="dhk8-fb__cart-actions">
            <Button variant="ghost" size="sm" icon aria-label="−" onClick={() => cart.decrement(l.lineId)}><Minus size={14} /></Button>
            <Button variant="ghost" size="sm" icon aria-label="+" onClick={() => cart.increment(l.lineId)}><Plus size={14} /></Button>
            <Button variant="ghost" size="sm" icon aria-label="Eliminar" onClick={() => cart.remove(l.lineId)}><Trash2 size={14} /></Button>
          </div>
        </li>
      ))}
    </ul>
  )
  const cartFootEl = (
    <footer className="dhk8-fb__cart-foot">
      <div className="dhk8-fb__cart-total-row"><span>Total</span><span>{formatCents(subtotal)}</span></div>
      <Button variant="primary" size="lg" block leading={<Banknote size={16} />} disabled={cart.lines.length === 0} onClick={handleCharge}>
        Cobrar {formatCents(subtotal)}
      </Button>
    </footer>
  )

  return (
    <div className="dhk8-fb">
      <div className="dhk8-fb__head">
        <div>
          <div className="dhk8-fb__eyebrow">Punto de venta · Modo rápido</div>
          <h1 className="dhk8-fb__title">Barra</h1>
          <div className="dhk8-fb__sub">Sin mesa · sin sub-cuentas · cobro directo</div>
        </div>
        <div className="dhk8-fb__head-actions">
          <Button
            variant={clientOpen ? 'primary' : 'secondary'}
            size="md"
            leading={<Monitor size={14} />}
            onClick={openClientDisplay}
            aria-label="Abrir pantalla de cliente"
            title={clientOpen ? 'Pantalla de cliente abierta — clic para enfocar' : 'Abrir pantalla de cliente en una segunda ventana'}
          >
            {clientOpen ? 'Pantalla de cliente · activa' : 'Abrir pantalla de cliente'}
          </Button>
          <Badge tone="primary">TAKEOUT</Badge>
        </div>
      </div>

      <div className="dhk8-fb__body">
        <section className="dhk8-fb__menu">
          <div className="dhk8-fb__search">
            <SearchIcon size={14} className="dhk8-fb__search-icon" />
            <input
              ref={searchInputRef}
              className="dhk8-fb__search-input"
              placeholder="Buscar producto o SKU…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleSearchEnter() }
              }}
              autoFocus
              aria-label="Buscar producto o SKU"
            />
          </div>
          <div className="dhk8-fb__cats">
            <button type="button" className={`dhk8-fb__cat${activeCat === 'ALL' ? ' dhk8-fb__cat--active' : ''}`} onClick={() => setActiveCat('ALL')}>Todos</button>
            {categories.map((c) => (
              <button key={c.id} type="button" className={`dhk8-fb__cat${activeCat === c.id ? ' dhk8-fb__cat--active' : ''}`} onClick={() => setActiveCat(c.id)}>{c.name}</button>
            ))}
          </div>
          <div className="dhk8-fb__grid">
            {menu.isLoading && <div className="dhk8-fb__empty">Cargando menú…</div>}
            {!menu.isLoading && filtered.map((p) => {
              const customizable = isCustomizable(p.id)
              const available = isAvailable86(p)
              const eightySixed = !available
              const busy = availBusyId === p.id
              return (
                <div key={p.id} className="dhk8-fb__cardwrap">
                  <button type="button" className={`dhk8-fb__card${eightySixed ? ' dhk8-fb__card--off' : ''}`} onClick={() => handleSelectProduct(p as unknown as ProductDto)}
                    aria-label={`${p.name} — ${formatCents(p.unitPriceCents)}${customizable ? ' — personalizable' : ''}${eightySixed ? ' — agotado' : ''}`}>
                    {customizable ? <Sparkles size={14} className="dhk8-fb__card-icon dhk8-fb__card-icon--cust" /> : <Coffee size={14} className="dhk8-fb__card-icon" />}
                    <div className="dhk8-fb__card-name">{p.name}</div>
                    <div className="dhk8-fb__card-meta">
                      {customizable && <span className="dhk8-fb__card-tag">Personalizar</span>}
                      {eightySixed && <span className="dhk8-fb__card-tag dhk8-fb__card-tag--off">Agotado</span>}
                      <span className="dhk8-fb__card-price">{formatCents(p.unitPriceCents)}</span>
                    </div>
                  </button>
                  {/* 86 / agotar: toggle de disponibilidad. Hermano de la card-button
                      (no anidado) para HTML válido + accesible. */}
                  <button
                    type="button"
                    className={`dhk8-fb__card-86${eightySixed ? ' dhk8-fb__card-86--off' : ''}`}
                    disabled={busy}
                    aria-label={eightySixed ? `Reactivar ${p.name}` : `Agotar ${p.name} (86)`}
                    aria-pressed={eightySixed}
                    title={eightySixed ? 'Marcar disponible de nuevo' : 'Marcar como agotado (86)'}
                    onClick={() => { if (!busy) void toggleAvailability(p.id, available) }}
                  >
                    {eightySixed ? '86' : <Ban size={13} />}
                  </button>
                </div>
              )
            })}
            {!menu.isLoading && filtered.length === 0 && <div className="dhk8-fb__empty">Sin productos que coincidan.</div>}
          </div>
        </section>

        <aside className="dhk8-fb__cart">
          <header className="dhk8-fb__cart-head">
            <div className="dhk8-fb__eyebrow">Carrito</div>
            <div className="dhk8-fb__cart-head-right">
              <Badge tone={cart.lines.length ? 'primary' : 'default'}>{itemCount} ítem{itemCount === 1 ? '' : 's'}</Badge>
              {/* Maximizar — solo móvil (CSS). Abre la orden completa en un sheet. */}
              {cart.lines.length > 0 && (
                <button type="button" className="dhk8-fb__cart-max" aria-label="Maximizar orden" title="Ver la orden completa" onClick={() => setCartMaxOpen(true)}>
                  <Maximize2 size={16} aria-hidden />
                </button>
              )}
            </div>
          </header>
          {cartListEl}
          {cartFootEl}
        </aside>
      </div>

      {/* Cobro mixto: reusa el PaymentDialog probado de Comandas (parciales +
          Restante + cierre exacto + idempotencia). redirectAfterTicket=null →
          se queda en /pos/barra. La orden ya está en el store (openFastSaleOrder). */}
      <PaymentDialog
        open={paymentOpen}
        redirectAfterTicket={null}
        autoPrintReceipt={false}
        onClosed={() => {
          // La orden se cerró (cubierta). Side-effects de BARRA: auto-print con la
          // config de barra (PaymentDialog hace drawer per-tender + print comanda)
          // + broadcast "Gracias" a la pantalla de cliente. best-effort.
          const order = useOrderStore.getState().order
          if (!order) return
          try {
            const cfg = getPrintingConfig()
            if (cfg.autoPrint.barra.enabled) {
              const header = getReceiptOpts(tenantIdActive)
              const receiptPayload = toReceipt(order, { header, copies: cfg.autoPrint.barra.copies })
              printReceipt(receiptPayload, { printer: cfg.defaultPrinter }).then(
                (r) => { if (r.jobId) trackRelayJob(r.jobId, 'Ticket', 'receipt'); else pushPrintHistory('receipt:barra', 'ok') },
                (e: unknown) => pushPrintHistory('receipt:barra', 'error', e instanceof Error ? e.message : String(e)),
              )
            }
          } catch (e) {
            pushPrintHistory('post-sale', 'error', e instanceof Error ? e.message : String(e))
          }
          if (clientOpen && clientWin) {
            try {
              const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('dhk8-pos-barra') : null
              const closeSnap: CartSnapshot = {
                v: 1, ts: Date.now(),
                branding: { name: getBranding().name, logoUrl: getBranding().logoUrl },
                lines: [], subtotalCents: 0, promos: [], totalCents: 0,
                lastSale: { folio: order.folio, totalCents: order.totalCents },
              }
              bc?.postMessage({ type: 'cart', payload: closeSnap })
              bc?.close()
            } catch { /* ignore */ }
          }
        }}
        onClose={() => {
          setPaymentOpen(false)
          // El store usa el status ADAPTADO (no expone PARTIALLY_PAID). "Parcial"
          // = hay pagos registrados pero la orden no cerró → recuperable en Para-llevar.
          const { order, payments } = useOrderStore.getState()
          if (order?.status === 'CLOSED') {
            showToast(`Venta ${order.folio} cerrada`)
          } else if (order && payments.length > 0) {
            showToast(`Cobro pendiente — ${order.folio} quedó en Para llevar`, 'error')
          }
          cart.clear()
          useOrderStore.getState().resetOrder()
        }}
      />

      <AddItemDialog product={pickedProduct} onClose={() => setPickedProduct(null)} onConfirm={handleAddCustomized} />

      {/* Orden maximizada (móvil) — lista completa navegable + Cobrar, como la
          comanda. Cerrar (grip/scrim/Escape) = minimizar de vuelta al peek inline. */}
      <BottomSheet
        open={cartMaxOpen}
        onClose={() => setCartMaxOpen(false)}
        title={`Orden · ${itemCount} ítem${itemCount === 1 ? '' : 's'}`}
        ariaLabel="Orden actual"
        heightFraction={0.92}
        footer={cartFootEl}
      >
        {cartListEl}
      </BottomSheet>

      {toast && <div className={`dhk8-fb__toast dhk8-fb__toast--${toast.kind}`}>{toast.msg}</div>}
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-fb { display:flex; flex-direction:column; gap:var(--pos-space-4); height:100%; min-height:0; }
  .dhk8-fb__head { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--pos-space-3); }
  .dhk8-fb__head-actions { display:flex; align-items:center; gap:var(--pos-space-2); }
  .dhk8-fb__eyebrow { font-family:var(--font-mono); font-size:var(--pos-text-xxs); letter-spacing:.12em; text-transform:uppercase; color:var(--pos-fg-tertiary); }
  .dhk8-fb__title { font-family:var(--font-display, system-ui); font-size:var(--pos-text-2xl); font-weight:var(--pos-weight-semibold); color:var(--pos-fg); margin:var(--pos-space-1) 0 0; }
  .dhk8-fb__sub { font-family:var(--font-mono); font-size:var(--pos-text-xs); color:var(--pos-fg-secondary); margin-top:2px; }
  .dhk8-fb__body { display:grid; grid-template-columns:1fr minmax(280px,360px); gap:var(--pos-space-4); flex:1; min-height:0; min-width:0; overflow-x:hidden; }
  .dhk8-fb__menu { display:flex; flex-direction:column; gap:var(--pos-space-3); min-height:0; min-width:0; }
  .dhk8-fb__search { display:flex; align-items:center; gap:var(--pos-space-2); padding:0 var(--pos-space-3); border:1px solid var(--pos-separator-strong); background:var(--pos-surface); height:var(--pos-touch-target, 44px); border-radius:var(--pos-radius-lg); transition:border-color var(--pos-duration-fast) ease, box-shadow var(--pos-duration-fast) ease; }
  .dhk8-fb__search:focus-within { border-color:var(--pos-tint); box-shadow:0 0 0 3px var(--pos-tint-soft); }
  .dhk8-fb__search-icon { color:var(--pos-fg-tertiary); }
  .dhk8-fb__search-input { flex:1; border:none; background:transparent; font-size:var(--pos-text-md); color:var(--pos-fg); outline:none; }
  .dhk8-fb__cats { display:flex; gap:var(--pos-space-2); overflow-x:auto; padding-bottom:var(--pos-space-1); }
  .dhk8-fb__cat { padding:var(--pos-space-2) var(--pos-space-4); border:1px solid var(--pos-separator-strong); background:transparent; color:var(--pos-fg-secondary); font-size:var(--pos-text-sm); font-weight:var(--pos-weight-medium); letter-spacing:.01em; cursor:pointer; white-space:nowrap; border-radius:var(--pos-radius-pill); min-height:var(--pos-touch-target); transition:all var(--pos-duration-fast) ease; }
  .dhk8-fb__cat:hover { background:var(--pos-elevated); }
  .dhk8-fb__cat--active { background:var(--pos-tint); color:#fff; border-color:var(--pos-tint); box-shadow:var(--pos-shadow-1); }
  .dhk8-fb__grid { flex:1; overflow-y:auto; display:grid; grid-template-columns:repeat(auto-fill,minmax(190px,1fr)); gap:var(--pos-space-3); align-content:start; }
  .dhk8-fb__cardwrap { position:relative; display:flex; }
  .dhk8-fb__card { flex:1; min-width:0; display:flex; flex-direction:column; gap:var(--pos-space-1); padding:var(--pos-space-3); background:var(--pos-surface); border:1px solid var(--pos-separator); color:var(--pos-fg); text-align:left; cursor:pointer; min-height:88px; border-radius:var(--pos-radius-lg); box-shadow:var(--pos-shadow-1); transition:transform var(--pos-duration-fast) ease, background var(--pos-duration-fast) ease, box-shadow var(--pos-duration-fast) ease; }
  .dhk8-fb__card:hover { background:var(--pos-elevated); box-shadow:var(--pos-shadow-2); }
  .dhk8-fb__card:active { transform:scale(.97); background:var(--pos-tint-soft); }
  /* 86 / agotado: card atenuada (pero sigue clicable para reactivar). */
  .dhk8-fb__card--off { opacity:.5; }
  .dhk8-fb__card--off:hover { opacity:.6; }
  /* 86 toggle — botón redondo arriba a la derecha (hermano de la card). Default =
     "Agotar" (icono prohibido); cuando ya está agotado muestra "86" en verde. */
  .dhk8-fb__card-86 { position:absolute; top:6px; right:6px; z-index:2; padding:0; width:28px; height:28px; display:flex; align-items:center; justify-content:center; border-radius:var(--pos-radius-pill,999px); border:1px solid var(--pos-separator-strong); background:color-mix(in oklch, var(--pos-surface) 70%, transparent); color:var(--pos-fg-tertiary); font-family:var(--font-mono); font-size:11px; font-weight:var(--pos-weight-bold,700); cursor:pointer; transition:background var(--pos-duration-fast) ease, color var(--pos-duration-fast) ease; }
  .dhk8-fb__card-86:hover:not(:disabled) { background:color-mix(in oklch, var(--pos-danger) 16%, transparent); color:var(--pos-danger); border-color:color-mix(in oklch, var(--pos-danger) 45%, transparent); }
  .dhk8-fb__card-86:focus-visible { outline:2px solid var(--pos-tint); outline-offset:2px; }
  .dhk8-fb__card-86:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-fb__card-86--off { background:var(--pos-tint); color:#fff; border-color:var(--pos-tint); }
  .dhk8-fb__card-86--off:hover:not(:disabled) { background:var(--pos-tint); color:#fff; filter:brightness(1.08); }
  .dhk8-fb__card-tag--off { background:var(--pos-danger-bg,color-mix(in oklch,var(--pos-danger) 16%,transparent)); color:var(--pos-danger); }
  .dhk8-fb__card-icon { color:var(--pos-fg-tertiary); }
  .dhk8-fb__card-icon--cust { color:var(--pos-tint); }
  .dhk8-fb__card-name { flex:1; font-family:var(--font-sans); font-size:var(--pos-text-sm); line-height:1.2; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
  .dhk8-fb__card-meta { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-2); }
  .dhk8-fb__card-tag { font-size:9px; font-weight:var(--pos-weight-semibold); letter-spacing:.06em; text-transform:uppercase; padding:1px var(--pos-space-2); border-radius:var(--pos-radius-pill); background:var(--pos-tint-soft); color:var(--pos-tint); }
  .dhk8-fb__card-price { font-family:var(--font-mono); font-size:var(--pos-text-xs); font-variant-numeric:tabular-nums; color:var(--pos-fg-secondary); }
  .dhk8-fb__empty { grid-column:1/-1; padding:var(--pos-space-6); text-align:center; font-family:var(--font-mono); font-size:var(--pos-text-xs); color:var(--pos-fg-tertiary); }
  .dhk8-fb__cart { display:flex; flex-direction:column; background:var(--pos-surface); border:1px solid var(--pos-separator); min-height:0; border-radius:var(--pos-radius-lg); box-shadow:var(--pos-shadow-1); overflow:hidden; }
  .dhk8-fb__cart-head { display:flex; justify-content:space-between; align-items:center; padding:var(--pos-space-3) var(--pos-space-4); border-bottom:1px solid var(--pos-separator); }
  .dhk8-fb__cart-head-right { display:flex; align-items:center; gap:var(--pos-space-2); }
  /* Botón maximizar — oculto en desktop (el carrito ya es full-height inline);
     aparece en móvil/tablet-portrait (≤720) donde el peek se queda corto. */
  .dhk8-fb__cart-max { display:none; align-items:center; justify-content:center; width:32px; height:32px; padding:0; border:1px solid var(--pos-separator-strong); background:transparent; color:var(--pos-fg-secondary); border-radius:var(--pos-radius-md); cursor:pointer; -webkit-tap-highlight-color:transparent; transition:background var(--pos-duration-fast) ease, color var(--pos-duration-fast) ease; }
  .dhk8-fb__cart-max:hover { background:var(--pos-elevated); color:var(--pos-fg); }
  .dhk8-fb__cart-max:active { transform:scale(.95); }
  .dhk8-fb__cart-list { flex:1; overflow-y:auto; list-style:none; margin:0; padding:0; }
  .dhk8-fb__cart-empty { padding:var(--pos-space-6); text-align:center; font-family:var(--font-mono); font-size:var(--pos-text-xs); color:var(--pos-fg-tertiary); }
  .dhk8-fb__cart-item { padding:var(--pos-space-2) var(--pos-space-4); border-bottom:1px solid var(--pos-separator); display:flex; flex-direction:column; gap:var(--pos-space-1); }
  .dhk8-fb__cart-item-head { display:grid; grid-template-columns:40px 1fr auto; gap:var(--pos-space-2); align-items:baseline; }
  .dhk8-fb__cart-qty { font-family:var(--font-mono); font-size:var(--pos-text-sm); color:var(--pos-fg-tertiary); }
  .dhk8-fb__cart-name { display:flex; flex-direction:column; gap:1px; font-family:var(--font-sans); font-size:var(--pos-text-sm); color:var(--pos-fg); line-height:1.2; }
  .dhk8-fb__cart-mods { font-family:var(--font-mono); font-size:var(--pos-text-xxs); color:var(--pos-fg-tertiary); }
  .dhk8-fb__cart-mods--note { font-style:italic; }
  .dhk8-fb__cart-total { font-family:var(--font-mono); font-size:var(--pos-text-xs); font-variant-numeric:tabular-nums; color:var(--pos-fg); }
  .dhk8-fb__cart-actions { display:flex; gap:var(--pos-space-1); padding-left:40px; }
  .dhk8-fb__cart-foot { padding:var(--pos-space-4); border-top:1px solid var(--pos-separator); display:flex; flex-direction:column; gap:var(--pos-space-3); background:var(--pos-elevated); }
  .dhk8-fb__cart-total-row { display:flex; justify-content:space-between; font-family:var(--font-sans); font-size:var(--pos-text-lg); color:var(--pos-fg); }
  .dhk8-fb__receipt { display:flex; flex-direction:column; gap:var(--pos-space-2); min-width:280px; }
  .dhk8-fb__receipt-row { display:flex; justify-content:space-between; font-family:var(--font-mono); font-size:var(--pos-text-sm); font-variant-numeric:tabular-nums; padding:var(--pos-space-2); border-bottom:1px solid var(--pos-separator); }
  .dhk8-fb__ticket-preview-label { margin-top:var(--pos-space-3); font-size:var(--pos-text-xs); text-transform:uppercase; letter-spacing:.05em; color:var(--pos-fg-tertiary); }
  .dhk8-fb__ticket-preview { background:#fff; color:#000; padding:8mm 6mm; margin-top:var(--pos-space-2); border:1px dashed var(--pos-separator-strong); border-radius:var(--pos-radius-md); max-width:80mm; font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace; font-size:11px; line-height:1.35; }
  .dhk8-fb__ticket-preview hr { border:none; border-top:1px dashed #000; margin:4px 0; }
  .dhk8-fbck { display:flex; flex-direction:column; gap:var(--pos-space-3); min-width:340px; }
  .dhk8-fbck__total { display:flex; justify-content:space-between; padding:var(--pos-space-4); background:var(--pos-elevated); border:1px solid var(--pos-separator); font-family:var(--font-display, system-ui); font-size:var(--pos-text-xl); font-weight:var(--pos-weight-semibold); color:var(--pos-fg); border-radius:var(--pos-radius-lg); }
  .dhk8-fbck__change { display:flex; justify-content:space-between; padding:var(--pos-space-3); background:var(--pos-tint-soft); border:1px solid color-mix(in oklch, var(--pos-tint) 30%, transparent); border-radius:var(--pos-radius-md); font-size:var(--pos-text-md); font-variant-numeric:tabular-nums; color:var(--pos-success); }
  .dhk8-fbck__error { color:var(--pos-danger); background:color-mix(in oklch, var(--pos-danger) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-danger) 40%, transparent); padding:var(--pos-space-3); font-size:var(--pos-text-sm); border-radius:var(--pos-radius-md); }
  .dhk8-fb__toast { position:fixed; bottom:var(--pos-space-6); left:50%; transform:translateX(-50%); padding:var(--pos-space-3) var(--pos-space-5); font-size:var(--pos-text-sm); z-index:var(--pos-z-toast, 300); border:1px solid var(--pos-separator); background:var(--pos-elevated); border-radius:var(--pos-radius-pill); box-shadow:var(--pos-shadow-3); }
  .dhk8-fb__toast--success { border-color:color-mix(in oklch, var(--pos-tint) 50%, transparent); color:var(--pos-success); }
  .dhk8-fb__toast--error { background:color-mix(in oklch, var(--pos-danger) 16%, var(--pos-elevated)); border-color:color-mix(in oklch, var(--pos-danger) 40%, transparent); color:var(--pos-danger); }
  @media (max-width: 720px) {
    /* Tablet-portrait stack: menu on top, cart card below. The body becomes the ONE
       vertical scroller — the menu grid AND the cart list flow inside it, no nested
       scroll areas. The cart's foot ("Cobrar $X") stays stuck to the bottom of the body
       so it's always reachable; everything above it scrolls as a single column. */
    .dhk8-fb__body { display:flex; flex-direction:column; gap:var(--pos-space-3); overflow-y:auto; -webkit-overflow-scrolling:touch; }
    .dhk8-fb__menu { flex:0 0 auto; }
    .dhk8-fb__grid { overflow-y:visible; grid-template-columns:repeat(2,1fr); }
    .dhk8-fb__cart { flex:0 0 auto; }
    .dhk8-fb__cart-list { max-height:none; overflow:visible; }
    .dhk8-fb__cart-foot { position:sticky; bottom:0; z-index:1; }
    .dhk8-fb__cart-max { display:inline-flex; }
  }
  /* Phone (≤520px):
     · Cabecera limpia: se oculta el texto decorativo (eyebrow + sub) que en móvil se
       partía palabra-por-palabra y estorbaba; queda título + acciones apiladas.
     · Productos: 2 columnas de tamaño UNIFORME (grid-auto-rows fijo) que scrollean.
     · Carrito + "Cobrar" PINNEADO abajo (siempre visible, sin scroll). El checkout es
       un modal (FastCheckoutDialog), así que capar la altura del carrito no lo afecta. */
  @media (max-width: 520px) {
    .dhk8-fb__head { flex-direction:column; align-items:stretch; gap:var(--pos-space-2); }
    .dhk8-fb__eyebrow, .dhk8-fb__sub { display:none; }
    .dhk8-fb__head-actions { flex-wrap:wrap; }
    .dhk8-fb__body { overflow:hidden; }
    .dhk8-fb__menu { flex:1 1 auto; min-height:0; }
    .dhk8-fb__grid { grid-template-columns:repeat(2,1fr); grid-auto-rows:96px; overflow-y:auto; flex:1; min-height:0; }
    .dhk8-fb__cart { flex:0 0 auto; max-height:46vh; }
    .dhk8-fb__cart-list { flex:1; min-height:0; overflow-y:auto; max-height:none; }
    .dhk8-fb__cart-foot { position:static; }
    .dhk8-fb__receipt { min-width:0; }
    .dhk8-fbck { min-width:0; }
  }
`
